import {
  InfiniteListBase,
  RecordContextProvider,
  useGetIdentity,
  useListContext,
  useTimeout,
  useTranslate,
} from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, RotateCcw, Target, Users } from "lucide-react";
import { Link } from "react-router";

import { TopToolbar } from "../layout/TopToolbar";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { CompanyEmpty } from "./CompanyEmpty";
import { CompanyImportButton } from "./CompanyImportButton";
import {
  CompanyListFilter,
  CompanyListFilterSummary,
} from "./CompanyListFilter";
import { ImageList } from "./GridList";
import {
  SalesPriorityBadge,
  SroMembershipBadge,
} from "./CompanySalesIntelligence";

const COMPANY_SORT_FIELDS = [
  "lead_score",
  "lead_priority_rank",
  "sro_membership_status",
  "sro_membership_ended_at",
  "digital_maturity_score",
  "revenue_2025_thousand_rub",
  "size",
  "name",
  "created_at",
];

export const CompanyList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  return (
    <List
      title={false}
      perPage={25}
      sort={{ field: "lead_score", order: "DESC" }}
      actions={<CompanyListActions />}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
      <CompanyListLayout />
    </List>
  );
};

const CompanyListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (!data?.length && !hasFilters) return <CompanyEmpty />;

  return (
    <div className="w-full flex flex-row gap-8">
      <CompanyListFilter />
      <div className="flex flex-col flex-1 gap-4">
        <ImageList />
      </div>
    </div>
  );
};

export const CompanyListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "lead_score", order: "DESC" }}
      queryOptions={{
        onError: () => {
          /* Disable error notification as CompanyListContentMobile handles it */
        },
      }}
    >
      <CompanyListLayoutMobile />
    </InfiniteListBase>
  );
};

const CompanyListLayoutMobile = () => {
  const { isPending, data, error, filterValues } = useListContext<Company>();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) return <CompanyEmpty />;

  return (
    <div>
      <MobileHeader>
        <CompanyListFilter />
        <SortButton fields={COMPANY_SORT_FIELDS} />
      </MobileHeader>
      <MobileContent>
        <CompanyListFilterSummary />
        <CompanyListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};

const CompanyListContentMobile = () => {
  const translate = useTranslate();
  const {
    data: companies,
    error,
    isPending,
    refetch,
  } = useListContext<Company>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) return null;

    return (
      <div className="flex flex-col gap-3">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex items-start gap-3 py-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="min-w-0 flex-1">
              <Skeleton className="mb-2 h-5 w-44" />
              <Skeleton className="h-4 w-56" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && !companies) {
    return (
      <div className="p-4">
        <div className="mb-4 text-center text-muted-foreground">
          {translate("resources.companies.list.error_loading", {
            _: "Could not load companies.",
          })}
        </div>
        <div className="text-center">
          <Button onClick={() => refetch()}>
            <RotateCcw />
            {translate("crm.common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y">
      {(companies ?? []).map((company) => (
        <RecordContextProvider key={company.id} value={company}>
          <CompanyItemContentMobile company={company} />
        </RecordContextProvider>
      ))}
      {companies?.length === 0 && (
        <div className="p-4 text-muted-foreground">
          {translate("resources.companies.empty.title")}
        </div>
      )}
    </div>
  );
};

const CompanyItemContentMobile = ({ company }: { company: Company }) => {
  const translate = useTranslate();
  const location = [company.city, company.state_abbr || company.country]
    .filter(Boolean)
    .join(", ");
  const size =
    typeof company.size === "number" && Number.isFinite(company.size)
      ? company.size
      : null;

  return (
    <Link
      to={`/companies/${company.id}/show`}
      className="flex items-start gap-3 py-3 transition-colors hover:bg-muted"
    >
      <CompanyAvatar />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="line-clamp-2 font-medium leading-5">
            {company.name}
          </div>
          <div className="flex flex-col items-end gap-1">
            <SalesPriorityBadge priority={company.lead_priority} />
            {company.sro_membership_status === "ceased" ? (
              <SroMembershipBadge status={company.sro_membership_status} />
            ) : null}
          </div>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {company.tax_identifier && (
            <span>
              {translate("resources.companies.fields.tax_identifier_short", {
                _: "Tax ID",
              })}
              : {company.tax_identifier}
            </span>
          )}
          {location && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-44 truncate">{location}</span>
            </span>
          )}
          {size !== null && (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {translate("resources.companies.auditors_count_value", {
                smart_count: size,
              })}
            </span>
          )}
          {company.lead_score != null && (
            <span className="inline-flex items-center gap-1">
              <Target className="h-3.5 w-3.5" />
              {translate("resources.companies.sales.score", {
                score: company.lead_score,
              })}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const CompanyListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <CompanyImportButton />
      <SortButton fields={COMPANY_SORT_FIELDS} />
      <ExportButton />
      <CreateButton
        label={translate("resources.companies.action.new", {
          _: "New Company",
        })}
      />
    </TopToolbar>
  );
};
